import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Clock, MapPin, Phone, Car, User, CheckCircle, Navigation, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TRIP_STAGES = [
  { id: 'quoted', label: 'Quote Sent', icon: '1' },
  { id: 'confirmed', label: 'Confirmed', icon: '2' },
  { id: 'dispatched', label: 'Dispatched', icon: '3' },
  { id: 'en_route', label: 'En Route', icon: '4' },
  { id: 'completed', label: 'Completed', icon: '5' },
  { id: 'billed', label: 'Billed', icon: '6' },
];

const RideTracker = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      // Try tracking first
      const trackRes = await fetch(`${API}/api/tracking/booking/${bookingId}`);
      if (trackRes.ok) setTracking(await trackRes.json());

      // Also fetch booking details from dispatch board
      const boardRes = await fetch(`${API}/api/dispatch/board`);
      if (boardRes.ok) {
        const data = await boardRes.json();
        const allBookings = Object.values(data.board || {}).flat();
        const found = allBookings.find(b => b.bookingId === bookingId);
        if (found) {
          setBooking(found);
          setLoading(false);
          return;
        }
      }

      // Fallback: search all bookings
      const bookingsRes = await fetch(`${API}/api/bookings`);
      if (bookingsRes.ok) {
        const bData = await bookingsRes.json();
        const found = (bData.bookings || bData || []).find(b => b.bookingId === bookingId);
        if (found) setBooking(found);
        else setError('Booking not found');
      }
    } catch (err) {
      console.error(err);
      setError('Unable to load booking');
    }
    setLoading(false);
  }, [bookingId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (booking?.trip_status === 'billed' || booking?.trip_status === 'completed') return;
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [booking?.trip_status, loadData]);

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-red-600 border-t-transparent mx-auto mb-4" />
        <p className="text-gray-400 text-sm">Loading your ride...</p>
      </div>
    </div>
  );

  if (error || !booking) return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <Card className="max-w-sm w-full p-8 text-center bg-gray-900 border-gray-800">
        <div className="h-16 w-16 rounded-full bg-red-900/30 flex items-center justify-center mx-auto mb-4">
          <Car className="h-8 w-8 text-red-500" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Booking Not Found</h2>
        <p className="text-gray-400 text-sm mb-4">This tracking link may have expired or the booking ID is invalid.</p>
        <p className="text-xs text-gray-500">Ref: {bookingId}</p>
      </Card>
    </div>
  );

  const ci = booking.customerInfo || {};
  const tripStatus = booking.trip_status || 'quoted';
  const currentIdx = TRIP_STAGES.findIndex(s => s.id === tripStatus);
  const isComplete = tripStatus === 'completed' || tripStatus === 'billed';
  const isEnRoute = tripStatus === 'en_route';
  const driver = booking.dispatch;

  const statusMessages = {
    quoted: 'Your booking request has been received. Our team will confirm shortly.',
    confirmed: 'Your ride is confirmed! A driver will be assigned soon.',
    dispatched: 'A driver has been assigned and is preparing for your pickup.',
    en_route: 'Your driver is on the way! They should arrive at your pickup location soon.',
    completed: 'Your ride has been completed. Thank you for riding with ACE!',
    billed: 'Your ride is complete and has been invoiced. Thank you!',
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ride-tracker">
      {/* Header */}
      <div className="border-b border-gray-800">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] text-red-500 font-bold tracking-widest uppercase">ACE Live Tracker</p>
            <h1 className="text-lg font-bold">Booking {bookingId}</h1>
          </div>
          <div className={`h-3 w-3 rounded-full ${isComplete ? 'bg-green-500' : 'bg-red-500 animate-pulse'}`} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
        {/* Status Banner */}
        <Card className={`p-5 border-0 ${isComplete ? 'bg-green-600' : isEnRoute ? 'bg-red-600' : 'bg-gray-800'} text-white`} data-testid="status-banner">
          <p className="text-xs opacity-70 mb-1 uppercase tracking-wider">Current Status</p>
          <h2 className="text-2xl font-bold capitalize" data-testid="current-trip-status">{tripStatus.replace(/_/g, ' ')}</h2>
          <p className="text-sm opacity-80 mt-2">{statusMessages[tripStatus]}</p>
          {tracking?.eta_minutes && isEnRoute && (
            <div className="flex items-center gap-2 mt-3 bg-white/20 rounded-full px-4 py-2 w-fit">
              <Clock className="h-4 w-4" />
              <span className="text-sm font-bold" data-testid="eta-display">ETA: {tracking.eta_minutes} min</span>
            </div>
          )}
        </Card>

        {/* Trip Status Pipeline */}
        <div className="relative" data-testid="trip-pipeline">
          {TRIP_STAGES.map((stage, i) => {
            const isDone = i <= currentIdx;
            const isCurrent = i === currentIdx;
            return (
              <div key={stage.id} className="flex items-start gap-4 relative">
                {/* Vertical line */}
                {i < TRIP_STAGES.length - 1 && (
                  <div className={`absolute left-[15px] top-[32px] w-0.5 h-8 ${isDone ? 'bg-green-500' : 'bg-gray-700'}`} />
                )}
                <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold transition-all
                  ${isCurrent ? 'bg-red-600 text-white ring-2 ring-red-400 ring-offset-2 ring-offset-black' : isDone ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-500 border border-gray-700'}`}>
                  {isDone ? <CheckCircle className="h-4 w-4" /> : stage.icon}
                </div>
                <div className={`pb-8 ${isCurrent ? 'text-white' : isDone ? 'text-green-400' : 'text-gray-600'}`}>
                  <p className={`text-sm font-semibold ${isCurrent ? '' : ''}`}>{stage.label}</p>
                  {isCurrent && <p className="text-xs opacity-60 mt-0.5">In progress...</p>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Ride Details */}
        <Card className="p-4 bg-gray-900 border-gray-800 space-y-3" data-testid="ride-details">
          <h3 className="text-sm font-bold text-red-500 uppercase tracking-wider mb-2">Ride Details</h3>
          <div className="space-y-2.5 text-sm">
            <div className="flex items-start gap-3">
              <Calendar className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-gray-400 text-xs">Date & Time</p>
                <p className="font-semibold">
                  {booking.pickupDate ? new Date(booking.pickupDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—'}
                  {booking.pickupTime ? ` at ${booking.pickupTime}` : ''}
                  {booking.dropoffTime ? ` → ${booking.dropoffTime}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-gray-400 text-xs">Pickup</p>
                <p className="font-semibold">{booking.pickupLocation || '—'}</p>
              </div>
            </div>
            {booking.dropoffLocation && (
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-gray-400 text-xs">Drop-off</p>
                  <p className="font-semibold">{booking.dropoffLocation}</p>
                </div>
              </div>
            )}
            {booking.stops?.length > 0 && (
              <div className="flex items-start gap-3">
                <Navigation className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-gray-400 text-xs">Stops</p>
                  {booking.stops.map((s, i) => <p key={i} className="font-semibold">{s.location}{s.notes ? ` (${s.notes})` : ''}</p>)}
                </div>
              </div>
            )}
            <div className="flex items-start gap-3">
              <Car className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-gray-400 text-xs">Service</p>
                <p className="font-semibold capitalize">{booking.serviceType?.replace(/-/g, ' ')}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Driver Info */}
        {driver?.driver_name && (
          <Card className="p-4 bg-gray-900 border-gray-800" data-testid="driver-info">
            <h3 className="text-sm font-bold text-red-500 uppercase tracking-wider mb-3">Your Driver</h3>
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-gray-800 flex items-center justify-center">
                <User className="h-7 w-7 text-gray-400" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-lg">{driver.driver_name}</p>
                {driver.vehicle_info && <p className="text-xs text-gray-400">{driver.vehicle_info}</p>}
              </div>
            </div>
            {driver.driver_phone && (
              <a href={`tel:${driver.driver_phone}`}
                className="mt-3 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-lg py-3 text-sm font-semibold transition-colors"
                data-testid="call-driver-btn">
                <Phone className="h-4 w-4" /> Call Driver
              </a>
            )}
          </Card>
        )}

        {/* Contact ACE */}
        <Card className="p-4 bg-gray-900 border-gray-800">
          <div className="flex items-center justify-center gap-3 mb-3">
            <a href={`${API}/api/calendar/booking/${bookingId}/google`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors"
              data-testid="add-google-calendar">
              <Calendar className="h-4 w-4" /> Add to Google Calendar
            </a>
            <a href={`${API}/api/calendar/booking/${bookingId}/ics`}
              className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors"
              data-testid="download-ics">
              <Calendar className="h-4 w-4" /> .ics
            </a>
          </div>
          <p className="text-xs text-gray-500 text-center mb-2">Need help? Contact ACE directly</p>
          <a href="tel:6788463009"
            className="flex items-center justify-center gap-2 text-red-500 hover:text-red-400 font-semibold transition-colors"
            data-testid="call-ace-btn">
            <Phone className="h-4 w-4" /> (678) 846-3009
          </a>
        </Card>

        {/* Auto-refresh indicator */}
        {!isComplete && (
          <p className="text-center text-[10px] text-gray-600 animate-pulse">
            Auto-refreshing every 30 seconds
          </p>
        )}
      </div>
    </div>
  );
};

export default RideTracker;
